import React, { useEffect, useState } from "react";
import {
    createSmartContract,
    getSmartContracts,
    addSmartContractClause,
} from "../../api/smartContractService";
import {
    approveLegalReview,
    rejectLegalReview,
} from "../../api/legalReviewService";

export default function SmartContractPage() {
    const [contracts, setContracts] = useState([]);
    const [shipmentId, setShipmentId] = useState("");
    const [selectedContractId, setSelectedContractId] = useState("");
    const [clauseType, setClauseType] = useState("Delivery");
    const [clauseText, setClauseText] = useState("");
    const [comment, setComment] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadContracts = async () => {
        try {
            setError("");
            const data = await getSmartContracts();
            setContracts(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Failed to load smart contracts.");
        }
    };

    useEffect(() => {
        loadContracts();
    }, []);

    const handleCreate = async () => {
        try {
            setError("");
            setSuccess("");

            await createSmartContract(shipmentId);
            setSuccess("Smart contract created.");
            setShipmentId("");
            await loadContracts();
        } catch (err) {
            setError(err?.message || "Failed to create smart contract.");
        }
    };

    const handleAddClause = async () => {
        try {
            setError("");
            setSuccess("");

            await addSmartContractClause(selectedContractId, {
                clauseType,
                text: clauseText,
                isMandatory: true,
            });

            setSuccess("Clause added.");
            setClauseText("");
            await loadContracts();
        } catch (err) {
            setError(err?.message || "Failed to add clause.");
        }
    };

    const handleApprove = async () => {
        try {
            setError("");
            setSuccess("");

            await approveLegalReview(selectedContractId, comment);
            setSuccess("Smart contract approved.");
            setComment("");
            await loadContracts();
        } catch (err) {
            setError(err?.message || "Failed to approve contract.");
        }
    };

    const handleReject = async () => {
        try {
            setError("");
            setSuccess("");

            await rejectLegalReview(selectedContractId, comment);
            setSuccess("Smart contract rejected.");
            setComment("");
            await loadContracts();
        } catch (err) {
            setError(err?.message || "Failed to reject contract.");
        }
    };

    return (
        <div className="max-w-7xl mx-auto p-6 bg-white rounded shadow">
            <h2 className="text-2xl font-bold mb-4">
                Smart Contract Legal Department
            </h2>

            {error && <p className="text-red-600 mb-3">{error}</p>}
            {success && <p className="text-green-600 mb-3">{success}</p>}

            <div className="border rounded p-4 mb-6">
                <h3 className="text-lg font-bold mb-3">Create Smart Contract</h3>

                <div className="flex gap-3">
                    <input
                        type="number"
                        value={shipmentId}
                        onChange={(e) => setShipmentId(e.target.value)}
                        placeholder="Shipment ID"
                        className="border p-2 rounded w-48"
                    />

                    <button
                        onClick={handleCreate}
                        className="bg-green-700 text-white px-4 py-2 rounded"
                    >
                        Create
                    </button>
                </div>
            </div>

            <div className="border rounded p-4 mb-6">
                <h3 className="text-lg font-bold mb-3">Legal Action</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input
                        type="number"
                        value={selectedContractId}
                        onChange={(e) => setSelectedContractId(e.target.value)}
                        placeholder="Smart Contract ID"
                        className="border p-2 rounded"
                    />

                    <input
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Legal comment"
                        className="border p-2 rounded"
                    />
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={handleApprove}
                        className="bg-blue-700 text-white px-4 py-2 rounded"
                    >
                        Approve
                    </button>

                    <button
                        onClick={handleReject}
                        className="bg-red-700 text-white px-4 py-2 rounded"
                    >
                        Reject
                    </button>
                </div>
            </div>

            <div className="border rounded p-4 mb-6">
                <h3 className="text-lg font-bold mb-3">Add Contract Clause</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <input
                        type="number"
                        value={selectedContractId}
                        onChange={(e) => setSelectedContractId(e.target.value)}
                        placeholder="Smart Contract ID"
                        className="border p-2 rounded"
                    />

                    <input
                        value={clauseType}
                        onChange={(e) => setClauseType(e.target.value)}
                        placeholder="Clause Type"
                        className="border p-2 rounded"
                    />

                    <input
                        value={clauseText}
                        onChange={(e) => setClauseText(e.target.value)}
                        placeholder="Clause Text"
                        className="border p-2 rounded"
                    />
                </div>

                <button
                    onClick={handleAddClause}
                    className="bg-purple-700 text-white px-4 py-2 rounded"
                >
                    Add Clause
                </button>
            </div>

            <div className="border rounded p-4">
                <h3 className="text-lg font-bold mb-3">Smart Contracts</h3>

                <table className="w-full border text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="border p-2">ID</th>
                            <th className="border p-2">Shipment</th>
                            <th className="border p-2">Contract Number</th>
                            <th className="border p-2">Status</th>
                            <th className="border p-2">Created</th>
                            <th className="border p-2">Approved</th>
                        </tr>
                    </thead>

                    <tbody>
                        {contracts.map((item) => (
                            <tr key={item.id}>
                                <td className="border p-2">{item.id}</td>
                                <td className="border p-2">{item.shipmentId}</td>
                                <td className="border p-2">
                                    {item.contractNumber || "�"}
                                </td>
                                <td className="border p-2">{item.status}</td>
                                <td className="border p-2">
                                    {item.createdAt
                                        ? new Date(item.createdAt).toLocaleString()
                                        : "�"}
                                </td>
                                <td className="border p-2">
                                    {item.approvedAt
                                        ? new Date(item.approvedAt).toLocaleString()
                                        : "�"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}